<template>
  <div class="relative w-full h-full flex pr-2 min-w-0">
    <!-- Y-axis labels -->
    <div class="h-full pl-2 pt-2 pb-8 pr-3 flex flex-col justify-between">
      <BaseText v-for="creditsLabel in yAxisLabels" :key="creditsLabel" type="body" size="xs" class="text-text-normal">
        {{ creditsLabel }}
      </BaseText>
    </div>
    <!-- Bar Graph -->
    <div class="flex-grow flex flex-col h-full min-w-0">
      <!-- Plot Lines and Bars -->
      <div class="flex-grow" style="padding-top: 16px; padding-bottom: 15px;">
        <div ref="graphArea" class="relative w-full h-full">
          <!-- Plot Lines -->
          <BackgroundRenderer 
            class="absolute top-0 left-0"
            :dimensions="renderAreaDimensions" 
            :num-plot-lines="yAxisLabels.length" 
            :use-secondary-plot-lines="false"
            dash-array="8 10"
            :stroke-width="1"
            :secondary-line-padding="0"
          />
          <!-- Bars -->
          <div class="absolute top-0 left-0 w-full h-full flex iems-end justify-between"
          :style="{ padding: `0 ${MONTH_LABEL_WIDTH / 2}px 0 ${MONTH_LABEL_WIDTH / 2}px` }">
            <div v-for="(bar, index) in barData" :key="`bar-${bar.fullMonth}`" class="relative w-0 h-full" :class="{ 'z-50': hoveredBarIndex === index }">
              <div class="graph-bar-container p-1" @mouseenter="hoveredBarIndex = index" @mouseleave="hoveredBarIndex = null"
              :class="{ 'pointer-events-none': bar.credit_count === 0 }">
                <div class="graph-bar" :class="{ 'current-month': index === barData?.length - 1, 'active': hoveredBarIndex === index }"
                :style="{ width: `${BAR_WIDTH}px`, '--bar-height': `${bar.height}px` }">
                  <!-- Bar Hover Tooltip -->
                  <transition>
                    <UsageGraphHoverTooltip 
                      v-if="hoveredBarIndex === index" 
                      class="bar-tooltip"
                      :credit-count="bar.credit_count"
                      :date="bar.date"
                    />
                  </transition>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- X-axis labels -->
      <div class="w-full h-6 flex items-start justify-between min-w-0">
        <BaseText v-for="monthLabel in xAxisLabels.slice(xAxisLabels.length - maxMonthsDisplayed)" 
        :key="monthLabel" type="body" size="xs" class="text-text-normal text-center w-full" 
        :style="{ maxWidth: `${MONTH_LABEL_WIDTH}px` }">
          {{ monthLabel }}
        </BaseText>
      </div>
      <!-- Empty State -->
      <div v-if="showEmptyState" class="absolute left-1/2 top-14 transform -translate-x-1/2 z-50
      flex flex-col items-center gap-1.5 py-4 px-6 rounded-lg bg-neutral-700 shadow-md"
      style="width: 224px;">
        <BaseText type="label" size="sm" class="text-white">
          No Usage Data
        </BaseText>
        <BaseText type="body" size="xs" class="text-neutral-alpha-700 text-center">
          We don't have any recent usage data for you yet
        </BaseText>
      </div>
    </div>
  </div>
</template>

<script>
import { scaleLinear } from 'd3-scale'
import moment from 'moment'

import BackgroundRenderer from '../lens/graphs/line/BackgroundRenderer.vue'
import UsageGraphHoverTooltip from './UsageGraphHoverTooltip.vue'

const BAR_WIDTH = 24
const MONTH_LABEL_WIDTH = 60

export default {
  name: 'ApiUsageBarGraph',
  components: {
    BackgroundRenderer,
    UsageGraphHoverTooltip
  },
  props: {
    usageTimestamps: {
      type: Array,
      default: () => []
    },
    maxMonthlyCredits: {
      type: Number,
      default: 10000
    }
  },
  data () {
    return {
      // Graph UI State
      axisScale: null,
      yAxisLabels: [],
      xAxisLabels: [],
      renderAreaDimensions: { width: 0, height: 0 },
      resizeObserver: null,
      maxMonthsDisplayed: 12,
      BAR_WIDTH,
      MONTH_LABEL_WIDTH,

      hoveredBarIndex: null 
    }
  },
  mounted () {
    this.$nextTick(() => {
      // Add a small delay to ensure DOM is fully rendered after conditional rendering
      setTimeout(() => {
        this.initializeAxisInformation()
        this.resizeObserver = new ResizeObserver(() => {
          this.computeGraphRenderArea()
        })
        if (this.$refs.graphArea) {
          this.resizeObserver.observe(this.$refs.graphArea)
        }
      }, 50)
    })
  },
  watch: {
    usageTimestamps: {
      handler () {
        if (this.usageTimestamps?.length) {
          this.initializeAxisInformation()
        }
      },
    }
  },
  computed: {
    barData () {
      if (!this.axisScale || !this.xAxisLabels.length || !this.usageTimestamps?.length) return []
      return this.usageTimestamps.map((data, index) => ({
        ...data,
        height: this.axisScale(data.credit_count),
        ...this.getDateLabels(data.date)
      })).reverse().slice(this.usageTimestamps.length - this.maxMonthsDisplayed)
    },
    showEmptyState () {
      return Math.max(...this.usageTimestamps?.map(timestamp => timestamp.credit_count)) === 0
    }
  },
  methods: {
    initializeAxisInformation () {
      if (!this.usageTimestamps?.length) return
      this.computeGraphRenderArea()
      this.computeAxisScale()
      this.computeYAxisLabels() // Credits Labels
      this.computeXAxisLabels() // Month Labels
    },
    computeAxisScale () {
      const maxCredits = Math.max(...this.usageTimestamps.map(timestamp => timestamp.credit_count))
      this.axisScale = scaleLinear()
        .domain([0, Math.max(maxCredits, this.maxMonthlyCredits)])
        .range([0, this.renderAreaDimensions.height])
    },
    computeYAxisLabels () {
      let ticks = this.axisScale.ticks(8)
      const reduceTicks = (ticks) => {
        // Ensures that the number of ticks is less than or equal to 8
        let tickValues = [...ticks]
        if (ticks.length % 2 === 0) {
          const newMax = ticks[ticks.length - 1] + ticks[1]
          tickValues.push(newMax)
          this.axisScale.domain([0, newMax])
        }
        while (tickValues.length > 8) {
          tickValues = tickValues.filter((_, index) => index % 2 === 0)
        }
        return tickValues
      }
      ticks = reduceTicks(ticks)

      // Ensure the highest tick covers the domain maximum
      if (ticks[ticks.length - 1] < this.axisScale.domain()[1]) {
        const newMax = ticks[ticks.length - 1] + ticks[1]
        ticks.push(newMax)
        this.axisScale.domain([0, newMax])
      }

      this.yAxisLabels = ticks.reverse().map(tick => tick.toLocaleString())
    },
    computeXAxisLabels () {
      const last12Months = []
      for (let i = 0; i < 12; i++) {
        last12Months.push(moment().subtract(i, 'month').format('MMM'))
      }
      this.xAxisLabels = last12Months.reverse()
    },
    computeGraphRenderArea () {
      const graphArea = this.$refs.graphArea
      if (!graphArea) return
      const { width, height } = graphArea.getBoundingClientRect()
      this.renderAreaDimensions = { width, height }
      this.maxMonthsDisplayed = Math.min(12, Math.floor(width / MONTH_LABEL_WIDTH))
    },
    getDateLabels (date) {
      // Date is in format YYYY-MM
      const [year, _] = date.split('-')
      return {
        year: `${year}`,
        fullMonth: moment(date, 'YYYY-MM').format('MMMM')
      }
    }
  }
}
</script>

<style scoped>
@property --outline-color {
  syntax: '<color>';
  inherits: false;
  initial-value: transparent;
}
.graph-bar-container {
  position: absolute;
  bottom: -4px;
  left: 0px;
  transform: translateX(-50%);
}
.graph-bar {
  --bar-color: rgba(6, 7, 16, 0.92);
  border-radius: 3px;
  background-color: var(--bar-color);
  height: var(--bar-height);
  animation: barEnter 0.5s ease-out forwards;

  --outline-color: transparent;
  outline: 2px solid var(--outline-color);
  outline-offset: 4px;
  transition: --outline-color 150ms ease-in-out;
}
.graph-bar.current-month {
  --bar-color: #00CBA8;
}
.graph-bar.active {
  --outline-color: var(--bar-color);
}
@keyframes barEnter {
  from { height: 0px; }
  to { height: var(--bar-height); }
}

.bar-tooltip {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: calc(100% + 12px);
  z-index: 100000;
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>